import React, { useState } from 'react';
import { ArrowLeft, HelpCircle, Search, Home, Heart, Sparkles, MessageCircle, Gamepad2, Image, Save, Users, Trophy, CreditCard, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { helpContent } from '../data/helpContent';
import ChatWidget from '../components/support/ChatWidget';

const HelpCenter = () => {
  const [activeCategory, setActiveCategory] = useState('start-here');
  const [searchTerm, setSearchTerm] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user came from within the app
  const cameFromApp = location.state?.from || 
                      (document.referrer && document.referrer.includes('/app/')) ||
                      (window.location.pathname.startsWith('/app/'));

  const handleBackClick = () => {
    if (location.state?.from) {
      // Navigate back to the specific page they came from
      navigate(location.state.from);
    } else if (cameFromApp) {
      // If they're in the app context, go to profile
      navigate('/app/profile');
    } else {
      // Otherwise navigate back using browser history or go to home
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/');
      }
    }
  };

  const categories = [
    { id: 'start-here', label: 'Start Here', icon: Home },
    { id: 'affirmations', label: 'Daily Affirmations', icon: Heart },
    { id: 'journaling', label: 'Journaling', icon: Sparkles },
    { id: 'conchi', label: 'Conchi AI Companion', icon: MessageCircle },
    { id: 'love-garden', label: 'Love Garden Game', icon: Gamepad2 },
    { id: 'divine-guidance', label: 'Divine Guidance', icon: Sparkles },
    { id: 'vision-board', label: 'Vision Board', icon: Image },
    { id: 'love-vault', label: 'Love Vault', icon: Save },
    { id: 'community', label: 'Community', icon: Users },
    { id: 'love-score', label: 'Love Score', icon: Trophy },
    { id: 'subscription', label: 'Plans & Billing', icon: CreditCard },
    { id: 'pwa', label: 'Install App', icon: Download },
  ];

  const filteredContent = searchTerm
    ? Object.keys(helpContent).reduce((acc, key) => {
        const category = helpContent[key];
        const filteredSections = category.sections.map(section => ({
          ...section,
          items: section.items.filter(
            item =>
              item.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
              item.a.toLowerCase().includes(searchTerm.toLowerCase())
          )
        })).filter(section => section.items.length > 0);

        if (filteredSections.length > 0) {
          acc[key] = { ...category, sections: filteredSections };
        }
        return acc;
      }, {})
    : { [activeCategory]: helpContent[activeCategory] };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-rose-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <button 
            onClick={handleBackClick}
            className="inline-flex items-center text-gray-600 hover:text-rose-600 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {cameFromApp ? 'Back to App' : 'Back to Home'}
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-rose-600 rounded-xl flex items-center justify-center">
              <HelpCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Help Center</h1>
              <p className="text-sm text-gray-600">Everything you need to know about Constant Love</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search */}
        <Card className="p-4 bg-white/70 backdrop-blur-sm border-rose-200 mb-6">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search help articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
            />
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-4 bg-white/70 backdrop-blur-sm border-rose-200 sticky top-4">
              <h3 className="font-semibold text-gray-800 mb-4">Topics</h3>
              <div className="space-y-1">
                {categories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <button
                      key={category.id}
                      onClick={() => {
                        setActiveCategory(category.id);
                        setSearchTerm('');
                      }}
                      className={`w-full text-left p-3 rounded-lg transition-colors flex items-center text-sm ${
                        activeCategory === category.id
                          ? 'bg-rose-500 text-white'
                          : 'text-gray-600 hover:bg-rose-50'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{category.label}</span>
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Content */}
          <div className="lg:col-span-3 space-y-6">
            {Object.keys(filteredContent).length === 0 && searchTerm && (
              <Card className="p-8 bg-white/70 backdrop-blur-sm border-rose-200 text-center">
                <p className="text-gray-500">No help articles found matching "{searchTerm}"</p>
                <p className="text-sm text-gray-400 mt-2">Try different keywords or browse by category</p>
              </Card>
            )}
            
            {Object.keys(filteredContent).map((key) => {
              const content = filteredContent[key];
              return (
                <div key={key}>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{content.title}</h2>
                  {content.sections.map((section, index) => (
                    <Card key={index} className="p-6 bg-white/70 backdrop-blur-sm border-rose-200 mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">{section.heading}</h3>
                      <div className="space-y-6">
                        {section.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="border-b border-gray-200 last:border-b-0 pb-4 last:pb-0">
                            <h4 className="font-medium text-gray-900 mb-2">{item.q}</h4>
                            <p className="text-gray-600 text-sm whitespace-pre-line">{item.a}</p>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Contact Support */}
        <Card className="mt-8 p-6 bg-white/70 backdrop-blur-sm border-rose-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Still Need Help?</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Button asChild variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50">
              <a href="mailto:support@constantloveco.com">Email Support</a>
            </Button>
            <Button asChild variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50">
              <Link to="/privacy-policy">Privacy Policy</Link>
            </Button>
            <Button asChild variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50">
              <Link to="/terms-of-service">Terms of Service</Link>
            </Button>
          </div>
        </Card>
      </div>

      {/* Chat Widget */}
      <ChatWidget isOpen={isChatOpen} onToggle={() => setIsChatOpen(!isChatOpen)} />
    </div>
  );
};

export default HelpCenter;